import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import http from "../services/http";
import Input from "./common/input";
import Select from "./common/select";
import Loading from "./common/loading";
import Pagination from "./common/pagination";
import { paginate } from "../utils/paginate";
import UserContext from "../context/userContext";
import SidebarContext from "../context/sidebarContext";

const ProcessTags = () => {
  const [user] = useContext(UserContext);
  const [sidebar] = useContext(SidebarContext);

  const [tagList, setTagList] = useState([]);
  const [tagname, setTagname] = useState("");
  const [selectTagname, setSelectTagname] = useState("");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const pageSize = 16;
  const navigation = '';
  
  const [iwbList, setIwbList] = useState([]);
  const [iwbCode, setIwbCode] = useState("");
  const [selectIwb, setSelectIwb] = useState("");
  const [iwbData, setIwbData] = useState([]);

  useEffect(() => {
    document.title = "FTR - Process Tags";
    return () => {
      document.title = "FTR";
    };
  }, []);

  useEffect(() => {
    async function getTagList() {
      try {
        const { data } = await http.get(`/phd/distinct`);
        setTagList(data);
      }
      catch {
        //ignore error
      }

      try {
        const { data: iwbData } = await http.get(`/iwb/distinct`);
        setIwbList(iwbData);
      }
      catch {
        //ignore error
      }
    }

    if (user) {
      getTagList();
    }
  }, [user]);

  useEffect(() => {
    async function getData() {
      setLoading(true);

      try {
        const { data } = await http.get(`/phd?tagname=${selectTagname}`);
        setData(data);
      }
      catch {
        //ignore error
      }

      setLoading(false);
    }

    if (selectTagname) {
      setTagname(selectTagname);
      getData(selectTagname);
    }
  }, [selectTagname, user]);

  useEffect(() => {
    async function getData() {
      setLoading(true);

      try {
        const { data } = await http.get(`/iwb?tagname=${selectIwb}`);
        setIwbData(data);
      }
      catch {
        //ignore error
      }

      setLoading(false);
    }
    
    if (selectIwb) {
      setIwbCode(selectIwb);
      getData(selectIwb);
    }
  }, [selectIwb, user]);

  async function handleSearch(e) {
    e.preventDefault();

    setLoading(true);
    try {
      const { data } = await http.get(`/phd?tagname=${tagname}`);
      setData(data);
    } catch (ex) {
      toast.error("Something went wrong");
    }
    setLoading(false);
  }

  const pagedDCSData = paginate(data, page, pageSize);
  const pagedIwbData = paginate(iwbData, page, pageSize);

  return (
    <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
      <h4>{tagList.length} Unique Tags from DCS</h4>
      <div className="row">
        <div className="col">
          <form>
            <div className="form-row">
              <div className="col-xl-6">
                <Input
                  name="tagname"
                  label="Tagname"
                  placeholder="Search..."
                  value={tagname}
                  onChange={(e) => setTagname(e.currentTarget.value)}
                />
              </div>
              <div className="col-xl-6">
                <Select
                  name="tagname"
                  label="All Tags"
                  placeholder="Tag"
                  value={selectTagname}
                  options={tagList}
                  onChange={(e) => setSelectTagname(e.currentTarget.value)}
                  listKey="TAGNAME"
                  listName="TAGNAME"
                />
              </div>
            </div>
            <div className="form-row">
              <button className="btn btn-primary" onClick={handleSearch}>
                Search
              </button>
            </div>
          </form>
          {loading ? (
            <Loading />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Tagname</th>
                  <th>Parameter</th>
                  <th>Timestamp (Real Time)</th>
                  <th>Value</th>
                  <th>Interval</th>
                  <th>Type</th>
                  <th>Loaded</th>
                </tr>
              </thead>
              <tbody>
                {pagedDCSData.map((record, i) => (
                  <tr key={i}>
                    <td>{record.TAGNAME}</td>
                    <td>{record.CODE}</td>
                    <td>
                      {moment.utc(record.TIMESTAMP).format("DD-MMM-YYYY HH:mm")}
                    </td>
                    <td>{record.VALUE}</td>
                    <td>{record.RETRIEVAL_TYPE}</td>
                    <td>{record.RETRIEVAL_INTERVAL}</td>
                    <td>
                      {record.LOADED === "Y" ? (
                        <span className="badge badge-success">Yes</span>
                      ) : (
                        <span className="badge badge-warning">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            itemsCount={data.length}
            pageSize={pageSize}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
            navigation={navigation}
          />
        </div>
      </div>
      <h4>{iwbList.length} Unique Tags from IWB</h4>
      <div className="row">
        <div className="col">
          <form>
            <div className="form-row">
              <div className="col-xl-6">
                <Input
                  name="iwb-code"
                  label="IWB Code"
                  placeholder="Search..."
                  value={iwbCode}
                  onChange={(e) => setIwbCode(e.currentTarget.value)}
                />
              </div>
              <div className="col-xl-6">
                <Select
                  name="iwb-codes"
                  label="All Codes"
                  placeholder="Code"
                  value={selectIwb}
                  options={iwbList}
                  onChange={(e) => setSelectIwb(e.currentTarget.value)}
                  listKey="PRODUCT_CODE"
                  listName="PRODUCT_CODE"
                />
              </div>
            </div>
            <div className="form-row">
              <button className="btn btn-primary" onClick={handleSearch}>
                Search
              </button>
            </div>
          </form>
          {loading ? (
            <Loading />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>IWB Code</th>
                  <th>Product</th>
                  <th>Parameter</th>
                  <th>Timestamp (Real Time)</th>
                  <th>Value</th>
                  <th>Site</th>
                  <th>Loaded</th>
                </tr>
              </thead>
              <tbody>
                {pagedIwbData.map((record, i) => (
                  <tr key={i}>
                    <td>{record.PRODUCT_CODE}</td>
                    <td>{record.PRODUCT_NAME}</td>
                    <td>{record.CODE}</td>
                    <td>
                      {moment
                        .utc(record.DAY_ENDING)
                        .format("DD-MMM-YYYY HH:mm")}
                    </td>
                    <td>{record.NET_WEIGHT}</td>
                    <td>{record.SITE}</td>
                    <td>
                      {record.LOADED ? (
                        <span className="badge badge-success">Yes</span>
                      ) : (
                        <span className="badge badge-warning">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            itemsCount={data.length}
            pageSize={pageSize}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
            navigation={navigation}
          />
        </div>
      </div>
    </div>
  );
};

export default ProcessTags;
